import { useCallback, useEffect, useRef, useState } from 'react';
import { getGate, notifyQualityChanged, startRun } from '../../services/QualityService';

// How often a run's gate is read, and for how long (about 20 minutes, then the project is left as "still running": its
// own Quality tab says more). An object so a test can make it fast.
export const qualityRunTiming = { pollMs: 3000, maxPolls: 400 };
const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Who may run the checks of a project (a run is a write: an editor or the owner): `{ eligible, skipped }`, the skipped
 * ones with why, as the Projects table's bulk actions say it.
 */
export function qualityPlan(projects) {
  const eligible = [];
  const skipped = [];
  (projects || []).forEach((project) => {
    const name = project.name || project.projectId;
    if (project.role === 'owner' || project.role === 'editor') { eligible.push({ projectId: project.projectId, name }); } else {
      skipped.push({ projectId: project.projectId, name, reason: 'you can only look at it' });
    }
  });
  return { eligible, skipped };
}

/**
 * "Run all checks" (the workspace's Quality tab) and "Run quality" (the Projects table's selection): starts each
 * project's quality run (services/QualityService.js startRun; a run already going on is waited for, not an error),
 * then reads each one's gate until its run ends. `runs`: `{ [projectId]: { name, status: 'running' | 'done' | 'failed'
 * | 'timeout', error? } }`; `active` while any runs; `onFinished` once all have ended (the page reloads the quality).
 */
export default function useQualityRuns({ onFinished } = {}) {
  const [runs, setRuns] = useState({});
  const alive = useRef(true);
  const finished = useRef(onFinished);
  finished.current = onFinished;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const set = (projectId, value) => { if (alive.current) { setRuns((now) => ({ ...now, [projectId]: { ...now[projectId], ...value } })); } };

  const runOne = useCallback(async({ projectId, name }) => {
    set(projectId, { name, status: 'running', error: null });
    try {
      await startRun(projectId, { trigger: 'manual' });
    } catch (failure) {
      if (failure?.code !== 'busy') { set(projectId, { status: 'failed', error: failure?.message || 'the run could not start' }); return; }
    }
    for (let poll = 0; poll < qualityRunTiming.maxPolls && alive.current; poll += 1) {
      // eslint-disable-next-line no-await-in-loop -- one look at a time, a few seconds apart
      await wait(qualityRunTiming.pollMs);
      try {
        // eslint-disable-next-line no-await-in-loop
        const answer = await getGate(projectId);
        if (!answer?.running) {
          notifyQualityChanged(projectId);
          set(projectId, { status: 'done' });
          return;
        }
      } catch (failure) {
        set(projectId, { status: 'failed', error: failure?.message || 'the run could not be followed' });
        return;
      }
    }
    set(projectId, { status: 'timeout' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- `set` only writes state

  const start = useCallback(async(entries) => {
    if (!entries?.length) { return; }
    await Promise.all(entries.map(runOne));
    if (alive.current) { finished.current?.(); }
  }, [runOne]);

  const values = Object.values(runs);
  const active = values.some((entry) => entry.status === 'running');
  const summary = values.length === 0 ? null : {
    total: values.length,
    ended: values.filter((entry) => entry.status !== 'running').length,
    failed: values.filter((entry) => entry.status === 'failed'),
    timedOut: values.filter((entry) => entry.status === 'timeout'),
  };
  return { runs, active, summary, start, clear: () => setRuns({}) };
}
